import type React from "react";
import { useState } from "react";
// Third Party
import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";

// UI
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { FileUpload } from "@/components/FileUpload";
import { Spinner } from "@/components/ui/spinner";
import { FilePreviewModal } from "@/components/FilePreviewModal";
// Icons
import { ArrowLeft, Save } from "lucide-react";

// Services and Utils
import { postTaxExecution } from "@/services/tax-obligations";

// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

type TaxObligation = {
  companyId: string;
  obligationType: "PLAN" | "EXECUTION";
  yearly: number;
  profitTax: number;
  addIncomeTax: number;
  monthlyTax: number;
  valueAddedTax: number;
  exciseTax: number;
  dividendTax: number;
  otherTaxes: number;
  docFile: string;
};

// A simple input that formats the number instantly as the user types.

export const Route = createFileRoute(
  "/_layout-private/report/obligation/assign-tax-obligation/add-new"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const companyId = localStorage
    .getItem("companyId")
    ?.replace(/^"(.+)"$/, "$1");
  const yearly = Number(localStorage.getItem("yearly"));

  // State for form fields.
  const [formData, setFormData] = useState({
    yearly: yearly,
    profitTax: "",
    addIncomeTax: "",
    monthlyTax: "",
    valueAddedTax: "",
    exciseTax: "",
    dividendTax: "",
    otherTaxes: "",
    docFile: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.TAX_OBLIGATION_REPORT);

  // Mutation
  const mutation = useMutation({
    mutationFn: (data: TaxObligation) => {
      // Cast to the expected service type
      const executionData = {
        ...data,
        obligationType: "EXECUTION" as const,
      };
      return postTaxExecution(executionData);
    },
    onSuccess: () => {
      toast({
        title: "ເພີ່ມຂໍ້ມູນສຳເລັດ",
        description: "ເພີ່ມພັນທະໃຫ້ລັດ",
        variant: "success",
      });
      queryClient.invalidateQueries({
        queryKey: ["tax-obligations-Details", companyId, yearly],
      });
      navigate({ to: ".." });
    },
    onError: (error: { status?: boolean; message?: string }) => {
      let description = error.message || "ແຜນປີນີ້ມີຢູ່ແລ້ວ.";
      if (
        error.status === false &&
        error.message === "TAX_PLAN_ALREADY_EXISTS"
      ) {
        description = "ແຜນປີນີ້ມີຢູ່ແລ້ວ";
      }
      toast({
        title: "ເກີດຂໍ້ຜິດພາດ",
        description: description,
        variant: "destructive",
      });
    },
  });

  const handleInputChange = (name: string, value: string) => {
    // Special handling for yearly field
    if (name === "yearly") {
      if (name === "yearly") {
        setFormData((prev) => ({ ...prev, [name]: Number(value) }));
      } else {
        setFormData((prev) => ({ ...prev, [name]: value }));
      }
      return;
    }

    // For tax amount fields
    let processedValue = value.replace(/,/g, "");

    // Check if input is valid
    if (!/^[0-9().]*$/.test(processedValue)) {
      return;
    }

    // Handle parentheses
    if (processedValue.startsWith("(")) {
      // Remove all existing closing parentheses
      processedValue = processedValue.replace(/\)/g, "");
      // Add single closing parenthesis
      processedValue = processedValue + ")";
    } else {
      // Remove any stray parentheses if not starting with '('
      processedValue = processedValue.replace(/[()]/g, "");
    }

    // Handle decimal points
    if (processedValue.includes(".")) {
      const [integerPart, decimalPart] = processedValue.split(".");
      if (decimalPart && decimalPart.length > 2) {
        const cleanDecimal = decimalPart.replace(/\)/g, "");
        processedValue = `${integerPart}.${cleanDecimal.slice(0, 2)}${
          processedValue.endsWith(")") ? ")" : ""
        }`;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: processedValue }));
  };

  // Handle file upload.
  const handleFileUpload = (file: string) => {
    setFormData((prev) => ({ ...prev, docFile: file }));
  };

  // Sum the tax fields.
  const taxFieldNames = [
    "profitTax",
    "addIncomeTax",
    "monthlyTax",
    "valueAddedTax",
    "exciseTax",
    "dividendTax",
    "otherTaxes",
  ];
  const totalTax = taxFieldNames.reduce((sum, field) => {
    return (
      sum + parseParenthesisNumber(formData[field as keyof typeof formData])
    );
  }, 0);

  const formattedTotalTax = new Intl.NumberFormat().format(totalTax);

  function formatNumberWithCommas(value: string | number) {
    const stringValue = String(value || "");
    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;

    // Split number into integer and decimal parts
    const [integerPart, decimalPart] = numericValue.split(".");

    // Format integer part with commas
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

    // Combine with decimal part if it exists
    const formattedValue =
      decimalPart !== undefined
        ? `${formattedInteger}.${decimalPart}`
        : formattedInteger;

    return isNegative ? `(${formattedValue})` : formattedValue;
  }

  function parseParenthesisNumber(strValue: string | number) {
    if (!strValue) return 0;
    const stringValue = String(strValue).replace(/,/g, "");
    const isNegative = stringValue.startsWith("(") && stringValue.endsWith(")");
    const numericValue = isNegative ? stringValue.slice(1, -1) : stringValue;
    return isNegative ? -Number(numericValue) : Number(numericValue);
  }

  // Handle form submission.
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!formData.docFile) {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ກະລຸນາເລືອກຟາຍອັບໂຫຼດ",
        variant: "destructive",
      });
      return;
    }

    const submitData: TaxObligation = {
      companyId: companyId || "",
      obligationType: "EXECUTION" as const,
      yearly: Number(formData.yearly),
      profitTax: parseParenthesisNumber(formData.profitTax),
      addIncomeTax: parseParenthesisNumber(formData.addIncomeTax),
      monthlyTax: parseParenthesisNumber(formData.monthlyTax),
      valueAddedTax: parseParenthesisNumber(formData.valueAddedTax),
      exciseTax: parseParenthesisNumber(formData.exciseTax),
      dividendTax: parseParenthesisNumber(formData.dividendTax),
      otherTaxes: parseParenthesisNumber(formData.otherTaxes),
      docFile: formData.docFile,
    };

    console.log(submitData, "Data");

    setSubmitting(true);
    mutation.mutate(submitData, {
      onSettled: () => {
        setSubmitting(false);
      },
    });
  };

  // Update the total calculation

  return (
    <section>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link
                replace
                to="/report/obligation"
                search={{
                  active: "assignTaxObligations",
                  startYear: 0,
                  endYear: 0,
                }}
              >
                ລາຍງານແຜນມອບພັນທະອາກອນ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ສ້າງລາຍງານພັນທະອາກອນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/obligation",
                search: {
                  active: "assignTaxObligations",
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex gap-1 items-center mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ສ້າງລາຍງານພັນທະອາກອນ</CardTitle>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <p>
              ປະຈຳປີ <span className="text-red-500">*</span>
            </p>
            {/* <NumberInput
							name="yearly"
							value={formData.yearly}
							onChange={handleInputChange}
							placeholder="1,000,000"
							className="mb-4 w-1/5"
						/> */}

            <Input
              type="number"
              placeholder="1,000,000"
              value={formData.yearly}
              onChange={(e) => handleInputChange("yearly", e.target.value)}
              className="mb-4 w-1/5"
              readOnly
            />

            <Table className="mb-4 w-10/12">
              <TableHeader>
                <TableRow className="text-center bg-gray-100">
                  <TableHead colSpan={2}>ເນຶ້ອໃນລາຍງານ</TableHead>
                  <TableHead colSpan={2}>ມູນຄ່າອາກອນ (ກີບ)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {[
                  { label: "ອາກອນກຳໄລ", name: "profitTax" },
                  { label: "ອາກອນມອບເພີ່ມ", name: "addIncomeTax" },
                  { label: "ອາກອນເງິນເດືອນ", name: "monthlyTax" },
                  { label: "ອາກອນມູນຄ່າເພີ່ມ", name: "valueAddedTax" },
                  { label: "ອາກອນຊົມໃຊ້", name: "exciseTax" },
                  { label: "ອາກອນເງິນປັນຜົນ", name: "dividendTax" },
                  { label: "ອາກອນອື່ນໆ", name: "otherTaxes" },
                ].map(({ label, name }) => (
                  <TableRow key={name}>
                    <TableCell className="font-medium border" colSpan={2}>
                      {label}
                    </TableCell>
                    <TableCell className="font-medium border" colSpan={2}>
                      <Input
                        type="text"
                        name={name}
                        value={formatNumberWithCommas(
                          formData[name as keyof typeof formData]
                        )}
                        onChange={(e) =>
                          handleInputChange(name, e.target.value)
                        }
                        placeholder="1,000,000"
                        className="text-right"
                        inputMode="numeric"
                      />
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-[#E4E4E7]">
                  <TableCell colSpan={2}>ລວມມອບອາກອນໃນປີ</TableCell>
                  <TableCell colSpan={2} className="text-right">
                    {formattedTotalTax}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <div className="my-4 w-fit">
              <FileUpload
                currentFile={formData.docFile}
                onFileUpload={handleFileUpload}
              />
            </div>

            <div className="flex gap-x-3 justify-end">
              <Button
                onClick={() =>
                  navigate({
                    to: "/report/obligation",
                    search: {
                      active: "assignTaxObligations",
                      startYear: 0,
                      endYear: 0,
                    },
                  })
                }
                type="button"
                variant={"outline"}
              >
                ຍົກເລີກ
              </Button>
              <Button disabled={submitting} type="submit">
                {submitting ? (
                  <Spinner className="text-white" />
                ) : (
                  <>
                    <Save className="mr-2" /> ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </section>
  );
}

export default RouteComponent;
